import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Prompt, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  List,
  Modal,
  Radio,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  BellOutlined,
  CameraOutlined,
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  FormOutlined,
  MoreOutlined,
  NotificationOutlined,
  PlusOutlined,
  RollbackOutlined,
  SaveOutlined,
  SendOutlined,
  ShareAltOutlined,
  StopOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success, Error as ErrorMessage } from "../../../commons/Messages/Messages";
import {
  announce,
  decide,
  deleteChangeOrder,
  deletePhoto,
  getChangeOrder,
  pdfUrl,
  photoUrl,
  remind,
  revise,
  sendOffer,
  updateChangeOrder,
  uploadPhoto,
  withdraw
} from "./api";
import { normalizePosition, summarizeChangeOrder } from "./calc";
import { formatDateTime, formatDay, formatEuro, formatMinutes } from "../workReports/format";
import { shrinkPhoto } from "../workReports/photos";
import { SignatureForm } from "../workReports/SignDialog";
import PositionLine from "./PositionLine";
import ChangeOrderPreview from "./ChangeOrderPreview";
import ContractOverview from "./ContractOverview";
import {
  STATE_COLORS,
  contractLabels,
  legalBasis,
  pricingLabels,
  reasonHints,
  reasonLabels,
  stateLabels
} from "./labels";
import "../workReports/workReports.css";
import "./changeOrders.css";

const DRAFT_PREFIX = "change-order-draft:";
const EDITABLE = ["draft", "announced", "ordered"];
const DECIDABLE = ["draft", "announced", "offered", "ordered"];

let keySeed = 0;
const withKey = p => ({
  ...p,
  key: p.key || p._id || `p${Date.now().toString(36)}${(keySeed += 1)}`
});
const withoutKey = p => {
  const copy = { ...p };
  delete copy.key;
  return copy;
};

const readDraft = id => {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_PREFIX + id) || "null");
  } catch {
    return null;
  }
};
const writeDraft = (id, draft) => {
  try {
    if (draft) localStorage.setItem(DRAFT_PREFIX + id, JSON.stringify(draft));
    else localStorage.removeItem(DRAFT_PREFIX + id);
  } catch {
    // storage blocked: the server copy is still there
  }
};

const formFrom = data => ({
  title: data.title || "",
  description: data.description || "",
  siteAddress: data.siteAddress || "",
  taxRate: data.taxRate ?? 19,
  reason: data.changeOrder.reason,
  contractBasis: data.changeOrder.contractBasis,
  pricingBasis: data.changeOrder.pricingBasis,
  cause: data.changeOrder.cause || "",
  requestedBy: data.changeOrder.requestedBy || "",
  requestedAt: data.changeOrder.requestedAt || "",
  positions: (data.positions || []).map(withKey),
  photoCaptions: {}
});

const ChangeOrderEditor = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const { id } = useParams();
  const history = useHistory();
  const fileInput = useRef(null);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [uploading, setUploading] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialogValues, setDialogValues] = useState({});
  const [created, setCreated] = useState(null);
  const [signStep, setSignStep] = useState("review");

  const apply = next => {
    setData(next);
    setForm(formFrom(next));
    setDirty(false);
  };

  useEffect(() => {
    getChangeOrder(id)
      .then(res => {
        apply(res.data.data);
        const local = readDraft(id);
        if (
          local &&
          EDITABLE.includes(res.data.data.changeOrder.state) &&
          local.savedAt > Date.parse(res.data.data.updatedAt)
        ) {
          setDraft(local);
        } else if (local) writeDraft(id, null);
      })
      .catch(error => {
        generalErrorAlert(error);
        history.push("/user/change-orders");
      });
  }, [id]);

  useEffect(() => {
    if (dirty && form) writeDraft(id, { form, savedAt: Date.now() });
  }, [dirty, form]);

  const summary = useMemo(
    () =>
      form
        ? summarizeChangeOrder({
            positions: form.positions,
            taxRate: form.taxRate,
            pricingBasis: form.pricingBasis,
            cause: form.cause
          })
        : null,
    [form]
  );

  if (!data || !form || !summary) {
    return (
      <div className="change-orders-page">
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );
  }

  const co = data.changeOrder;
  const state = co.state;
  const readOnly = !EDITABLE.includes(state);
  const update = patch => {
    setForm(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const save = async ({ quiet = false } = {}) => {
    setSaving(true);
    try {
      const body = { ...form, positions: form.positions.map(withoutKey) };
      const res = await updateChangeOrder(id, body);
      apply(res.data.data);
      writeDraft(id, null);
      if (!quiet) Success(t("Saved"));
      return res.data.data;
    } catch (error) {
      generalErrorAlert(error);
      return null;
    } finally {
      setSaving(false);
    }
  };
  const saveIfDirty = async () => (dirty ? save({ quiet: true }) : data);

  const setPosition = (key, next) =>
    update({ positions: form.positions.map(p => (p.key === key ? { ...next, key } : p)) });
  const addPosition = () =>
    update({
      positions: [...form.positions, withKey(normalizePosition({ quantity: 1, unit: "Stk" }))]
    });

  const addPhotos = async files => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (dirty && !(await save({ quiet: true }))) return;
    setUploading(list.length);
    let latest = null;
    for (const file of list) {
      try {
        const { blob } = await shrinkPhoto(file);
        latest = (await uploadPhoto(id, blob)).data.data;
      } catch (error) {
        generalErrorAlert(error);
      } finally {
        setUploading(n => Math.max(0, n - 1));
      }
    }
    if (latest) apply(latest);
  };

  const removePhoto = photoId =>
    Modal.confirm({
      title: t("Remove this photo?"),
      okText: t("Remove"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          if (dirty && !(await save({ quiet: true }))) return;
          apply((await deletePhoto(id, photoId)).data.data);
        } catch (error) {
          generalErrorAlert(error);
        }
      }
    });

  const checkComplete = ({ positions = true } = {}) => {
    if (!form.cause.trim()) {
      ErrorMessage(t("Please describe what happened and why it is not in the contract."));
      return false;
    }
    if (positions && !form.positions.length && form.pricingBasis !== "hourly") {
      ErrorMessage(t("Add at least one position, or choose billing by the hour."));
      return false;
    }
    if (positions && form.positions.some(p => !String(p.description || "").trim())) {
      ErrorMessage(t("Every position needs a description."));
      return false;
    }
    return true;
  };

  const openDialog = async kind => {
    if (kind === "announce" && !checkComplete({ positions: false })) return;
    if (["offer", "sign"].includes(kind) && !checkComplete()) return;
    if (!(await saveIfDirty())) return;
    const email = data.clientInfo?.email || "";
    setDialogValues({
      email: kind === "remind" ? co.link?.sentTo || email : email,
      send: true,
      responseDays: 14,
      status: "approved",
      name: "",
      note: "",
      emailCopy: Boolean(email),
      reason: ""
    });
    setSignStep("review");
    setDialog(kind);
  };
  const setValue = patch => setDialogValues(prev => ({ ...prev, ...patch }));

  const run = async () => {
    setBusy(true);
    const v = dialogValues;
    try {
      if (dialog === "announce") {
        const res = await announce(id, { send: v.send, email: v.email });
        apply(res.data.data);
        Success(res.data.sentTo ? t("Sent to {{email}}.", { email: res.data.sentTo }) : t("Saved"));
        setDialog(null);
      } else if (dialog === "offer" || dialog === "remind") {
        const res =
          dialog === "offer"
            ? await sendOffer(id, { send: v.send, email: v.email, responseDays: v.responseDays })
            : await remind(id, { email: v.email });
        apply(res.data.data);
        setCreated(res.data);
        setDialog("link");
      } else if (dialog === "decide") {
        const res = await decide(id, {
          status: v.status,
          method: "MANUAL",
          name: v.name,
          note: v.note,
          emailCopy: v.emailCopy
        });
        apply(res.data.data);
        Success(t("Decision recorded."));
        setDialog(null);
      } else if (dialog === "withdraw") {
        apply((await withdraw(id, { reason: v.reason })).data.data);
        setDialog(null);
      }
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const signOnSite = async values => {
    setBusy(true);
    try {
      const res = await decide(id, {
        status: "approved",
        method: "ONSITE",
        name: values.name,
        image: values.image,
        emailCopy: values.emailCopy,
        email: values.email
      });
      apply(res.data.data);
      setDialog(null);
      Success(t("Approved. The positions are now part of the main order."));
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const takeBack = () =>
    Modal.confirm({
      title: t("Take the change order back?"),
      content: t("The customer's link stops working. You can edit and send it again."),
      okText: t("Take back"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          apply((await revise(id, {})).data.data);
        } catch (error) {
          generalErrorAlert(error);
        }
      }
    });

  const remove = () =>
    Modal.confirm({
      title: t("Delete this change order?"),
      okText: t("Delete"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await deleteChangeOrder(id);
          writeDraft(id, null);
          setDirty(false);
          history.push("/user/change-orders");
        } catch (error) {
          generalErrorAlert(error);
        }
      }
    });

  const share = async () => {
    const text = t("Please check change order {{number}}.", { number: co.number });
    if (navigator.share) {
      try {
        await navigator.share({ title: co.number, text, url: created.url });
        return;
      } catch {
        // cancelled: copy instead
      }
    }
    await navigator.clipboard?.writeText(created.url);
    Success(t("Link copied"));
  };

  const states = stateLabels(t);
  const reasons = reasonLabels(t);
  const hints = reasonHints(t);
  const canSend = ["draft", "announced", "ordered"].includes(state);
  const menu = [
    ...(["draft", "announced"].includes(state)
      ? [
          {
            key: "announce",
            icon: <NotificationOutlined />,
            label: t("Send cost notice"),
            onClick: () => openDialog("announce")
          }
        ]
      : []),
    ...(state === "offered"
      ? [
          {
            key: "remind",
            icon: <BellOutlined />,
            label: t("Send reminder"),
            onClick: () => openDialog("remind")
          },
          {
            key: "revise",
            icon: <RollbackOutlined />,
            label: t("Take back to edit"),
            onClick: takeBack
          }
        ]
      : []),
    ...(DECIDABLE.includes(state)
      ? [
          {
            key: "decide",
            icon: <FormOutlined />,
            label: t("Record the customer's decision"),
            onClick: () => openDialog("decide")
          },
          {
            key: "withdraw",
            icon: <StopOutlined />,
            label: t("Withdraw"),
            onClick: () => openDialog("withdraw")
          }
        ]
      : []),
    {
      key: "pdf",
      icon: <FilePdfOutlined />,
      label: t("PDF change order"),
      onClick: () => window.open(pdfUrl(id, "offer"), "_blank")
    },
    {
      key: "pdf2",
      icon: <FilePdfOutlined />,
      label: t("PDF cost notice"),
      onClick: () => window.open(pdfUrl(id, "announcement"), "_blank")
    },
    ...(state === "draft" && !co.announcedAt && !co.offeredAt
      ? [
          {
            key: "delete",
            icon: <DeleteOutlined />,
            danger: true,
            label: t("Delete"),
            onClick: remove
          }
        ]
      : [])
  ];

  const previewView = {
    number: co.number,
    title: form.title,
    reason: form.reason,
    contractBasis: form.contractBasis,
    pricingBasis: form.pricingBasis,
    cause: form.cause,
    description: form.description,
    requestedBy: form.requestedBy,
    requestedAt: form.requestedAt,
    responseDue: co.responseDue,
    siteAddress: form.siteAddress,
    positions: summary.positions,
    taxRate: summary.taxRate,
    totals: summary.totals,
    photos: co.photos,
    parent: data.parentInfo ? { label: data.parentInfo.label } : null,
    customer: data.clientInfo ? { name: data.clientInfo.name } : null,
    project: data.projectInfo ? { name: data.projectInfo.name } : null,
    decision: co.decision?.status ? co.decision : null
  };
  const historyLabels = {
    create: t("Created"),
    announce: t("Cost notice sent"),
    offer: t("Change order sent"),
    remind: t("Reminder sent"),
    revise: t("Taken back"),
    withdraw: t("Withdrawn"),
    approve: t("Approved"),
    reject: t("Rejected"),
    order: t("Ordered, price open"),
    remote_view: t("Opened by the customer")
  };

  return (
    <div className="change-orders-page">
      <Prompt when={dirty && !saving} message={t("You have unsaved changes. Leave anyway?")} />
      {draft && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={t("Unsaved changes from {{time}} were found on this device.", {
            time: dayjs(draft.savedAt).format("DD.MM.YYYY HH:mm")
          })}
          action={
            <Space direction="vertical" size={4}>
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  setForm(draft.form);
                  setDirty(true);
                  setDraft(null);
                }}
              >
                {t("Restore")}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  writeDraft(id, null);
                  setDraft(null);
                }}
              >
                {t("Discard")}
              </Button>
            </Space>
          }
        />
      )}

      <Card className="mb-3">
        <div className="co-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label={t("Back")}
            onClick={() => history.push("/user/change-orders")}
          />
          <div className="co-header-title">
            <Typography.Title level={4}>
              {co.number} {form.title}
            </Typography.Title>
            <Space size={4} wrap>
              <Tag color={STATE_COLORS[state]}>{states[state]}</Tag>
              {co.overdue && <Tag color="error">{t("Overdue")}</Tag>}
            </Space>
          </div>
          <Space wrap className={`co-header-actions${readOnly ? "" : " co-hide-phone"}`}>
            {!readOnly && (
              <Button
                icon={<SaveOutlined />}
                loading={saving}
                disabled={!dirty}
                onClick={() => save()}
              >
                {t("Save")}
              </Button>
            )}
            {canSend && (
              <Button
                type={state === "draft" ? "default" : "primary"}
                icon={<SendOutlined />}
                onClick={() => openDialog("offer")}
              >
                {t("Send to customer")}
              </Button>
            )}
            {DECIDABLE.includes(state) && (
              <Button
                type={state === "offered" ? "primary" : "default"}
                icon={<EditOutlined />}
                onClick={() => openDialog("sign")}
              >
                {t("Customer signs now")}
              </Button>
            )}
            <Dropdown menu={{ items: menu }} trigger={["click"]}>
              <Button icon={<MoreOutlined />} aria-label={t("More")} />
            </Dropdown>
          </Space>
        </div>
        {data.parentInfo && (
          <Typography.Paragraph type="secondary" className="co-parent">
            {t("Change order to")} <strong>{data.parentInfo.label}</strong> –{" "}
            {data.parentInfo.title}
            {data.clientInfo && ` · ${data.clientInfo.name}`}
          </Typography.Paragraph>
        )}
        {!data.parentInfo && data.clientInfo && (
          <Typography.Paragraph type="secondary" className="co-parent">
            {t("Without a main offer")} · {data.clientInfo.name}
            {data.projectInfo && ` · ${data.projectInfo.name}`}
          </Typography.Paragraph>
        )}
        {state === "announced" && co.announcedAt && (
          <Alert
            type="info"
            showIcon
            className="mt-2"
            message={t("Cost notice sent on {{date}}{{to}}.", {
              date: formatDateTime(co.announcedAt, lng),
              to: co.announcedTo ? ` – ${co.announcedTo}` : ""
            })}
          />
        )}
        {state === "offered" && (
          <Alert
            type={co.overdue ? "warning" : "info"}
            showIcon
            className="mt-2"
            message={
              co.overdue
                ? t("No answer since {{date}}. Send a reminder or record the decision.", {
                    date: formatDay(co.responseDue)
                  })
                : t("Waiting for the customer's answer until {{date}}.", {
                    date: formatDay(co.responseDue)
                  })
            }
          />
        )}
        {state === "ordered" && (
          <Alert
            type="warning"
            showIcon
            className="mt-2"
            message={t(
              "The customer ordered the work, the price is not agreed yet. Send the priced change order."
            )}
          />
        )}
        {state === "approved" && data.parentInfo && form.pricingBasis !== "hourly" && (
          <Alert
            type="success"
            showIcon
            className="mt-2"
            message={t(
              "Approved. The positions are now part of the main order and are invoiced with it."
            )}
            action={<Link to="/user/offers">{t("Offers")}</Link>}
          />
        )}
        {state === "approved" && form.pricingBasis === "hourly" && (
          <Alert
            type="success"
            showIcon
            className="mt-2"
            message={t(
              "Approved for work by the hour. Book the work reports on this change order and invoice them."
            )}
            action={<Link to="/user/work-reports">{t("Work reports")}</Link>}
          />
        )}
      </Card>

      {readOnly ? (
        <Card className="mb-3">
          <ChangeOrderPreview view={previewView} photoSrc={photoId => photoUrl(id, photoId)} />
        </Card>
      ) : (
        <>
          <Card className="mb-3" title={t("What changed")}>
            <Form layout="vertical">
              <Row gutter={[12, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item label={t("Title")} required>
                    <Input
                      value={form.title}
                      maxLength={200}
                      onChange={e => update({ title: e.target.value })}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("Reason")}
                    extra={
                      <>
                        {legalBasis(form.contractBasis, form.reason)}
                        {hints[form.reason] ? ` · ${hints[form.reason]}` : ""}
                      </>
                    }
                  >
                    <Select
                      value={form.reason}
                      options={Object.entries(reasons).map(([value, label]) => ({ value, label }))}
                      onChange={reason => update({ reason })}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("Contract")}>
                    <Select
                      value={form.contractBasis}
                      options={Object.entries(contractLabels(t)).map(([value, label]) => ({
                        value,
                        label
                      }))}
                      onChange={contractBasis => update({ contractBasis })}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("Pricing")}>
                    <Select
                      value={form.pricingBasis}
                      options={Object.entries(pricingLabels(t)).map(([value, label]) => ({
                        value,
                        label
                      }))}
                      onChange={pricingBasis => update({ pricingBasis })}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("Requested by")}>
                    <Input
                      value={form.requestedBy}
                      maxLength={200}
                      placeholder={t("e.g. site manager Meier")}
                      onChange={e => update({ requestedBy: e.target.value })}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label={t("On")}>
                    <DatePicker
                      className="co-full"
                      format="DD.MM.YYYY"
                      value={form.requestedAt ? dayjs(form.requestedAt) : null}
                      onChange={value =>
                        update({ requestedAt: value ? value.format("YYYY-MM-DD") : "" })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label={t("VAT rate")}>
                    <Select
                      value={form.taxRate}
                      options={[19, 7, 0].map(rate => ({ value: rate, label: `${rate} %` }))}
                      onChange={taxRate => update({ taxRate })}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label={t("Site address")}>
                    <Input
                      value={form.siteAddress}
                      maxLength={500}
                      onChange={e => update({ siteAddress: e.target.value })}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label={t("What happened and why it is not in the contract")} required>
                <Input.TextArea
                  value={form.cause}
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  maxLength={10000}
                  placeholder={t("e.g. When the wall was opened, the masonry was found to be wet.")}
                  onChange={e => update({ cause: e.target.value })}
                />
              </Form.Item>
              <Form.Item label={t("Description of the work")} className="mb-0">
                <Input.TextArea
                  value={form.description}
                  autoSize={{ minRows: 2, maxRows: 10 }}
                  maxLength={20000}
                  onChange={e => update({ description: e.target.value })}
                />
              </Form.Item>
            </Form>
          </Card>

          <Card
            className="mb-3 co-section"
            title={`${t("Positions")} (${form.positions.length})`}
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={addPosition}>
                {t("Add position")}
              </Button>
            }
          >
            {form.pricingBasis === "hourly" && (
              <Alert
                className="mb-3"
                type="info"
                showIcon
                message={t(
                  "Billed by the hour: positions are optional, e.g. as an estimate. The work is proven with work reports."
                )}
              />
            )}
            {form.positions.length ? (
              form.positions.map((position, index) => (
                <PositionLine
                  key={position.key}
                  index={index}
                  position={position}
                  computed={summary.positions[index]}
                  basePositions={(data.parentInfo?.positions || []).filter(
                    p => !p.changeOrderNumber
                  )}
                  readOnly={readOnly}
                  lng={lng}
                  onChange={next => setPosition(position.key, next)}
                  onRemove={() =>
                    update({ positions: form.positions.filter(p => p.key !== position.key) })
                  }
                />
              ))
            ) : (
              <Empty description={t("No positions yet.")} />
            )}
            <dl className="co-totals">
              <dt>{t("Net")}</dt>
              <dd className={summary.totals.net < 0 ? "co-negative" : ""}>
                {formatEuro(summary.totals.net, lng)}
              </dd>
              <dt>
                {t("VAT")} {summary.taxRate} %
              </dt>
              <dd>{formatEuro(summary.totals.tax, lng)}</dd>
              <dt className="co-gross">{t("Gross")}</dt>
              <dd className="co-gross">{formatEuro(summary.totals.gross, lng)}</dd>
            </dl>
          </Card>

          <Card
            className="mb-3 co-section"
            title={`${t("Photos")} (${co.photos.length})`}
            extra={
              <Button
                icon={<CameraOutlined />}
                loading={uploading > 0}
                onClick={() => fileInput.current?.click()}
              >
                {uploading > 0 ? t("Uploading {{n}} …", { n: uploading }) : t("Add photos")}
              </Button>
            }
          >
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              hidden
              onChange={event => {
                addPhotos(event.target.files);
                event.target.value = "";
              }}
            />
            {co.photos.length ? (
              <div className="co-photo-grid">
                <Image.PreviewGroup>
                  {co.photos.map(photo => (
                    <figure key={photo._id}>
                      <Image src={photoUrl(id, photo._id)} alt={photo.caption || ""} />
                      <Input
                        size="small"
                        placeholder={t("Caption")}
                        maxLength={300}
                        value={form.photoCaptions[photo._id] ?? photo.caption}
                        onChange={e =>
                          update({
                            photoCaptions: { ...form.photoCaptions, [photo._id]: e.target.value }
                          })
                        }
                      />
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={t("Remove")}
                        className="co-photo-remove"
                        onClick={() => removePhoto(photo._id)}
                      />
                    </figure>
                  ))}
                </Image.PreviewGroup>
              </div>
            ) : (
              <Empty
                description={t("Photos of what you found make the change order hard to dispute.")}
              />
            )}
          </Card>
        </>
      )}

      {data.parentInfo?.overview && (
        <ContractOverview overview={data.parentInfo.overview} currentId={id} lng={lng} />
      )}

      {(form.pricingBasis === "hourly" || data.workReports.length > 0) && (
        <Card
          className="mb-3"
          size="small"
          title={`${t("Work reports")} (${data.workReports.length})`}
        >
          {data.workReports.length ? (
            <List
              size="small"
              dataSource={data.workReports}
              renderItem={r => (
                <List.Item>
                  <Link to={`/user/work-reports/${r._id}`}>{r.number}</Link> · {formatDay(r.date)} ·{" "}
                  {formatMinutes((r.totals?.workMinutes || 0) + (r.totals?.travelMinutes || 0))} h ·{" "}
                  {formatEuro(r.totals?.net, lng)}
                </List.Item>
              )}
            />
          ) : (
            <Typography.Text type="secondary">
              {t("Choose this change order in a work report to book the hours on it.")}
            </Typography.Text>
          )}
        </Card>
      )}

      {co.history?.length > 0 && (
        <Card className="mb-3" size="small" title={t("History")}>
          <List
            size="small"
            dataSource={[...co.history].reverse()}
            renderItem={entry => (
              <List.Item>
                <Typography.Text type="secondary" className="co-num">
                  {formatDateTime(entry.at, lng)}
                </Typography.Text>{" "}
                {historyLabels[entry.action] || entry.action}
                {entry.note ? ` · ${entry.note}` : ""}
              </List.Item>
            )}
          />
        </Card>
      )}

      {!readOnly && (
        <div className="co-bottom-bar">
          <Button
            size="large"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!dirty}
            onClick={() => save()}
          >
            {t("Save")}
          </Button>
          <Button
            size="large"
            type="primary"
            icon={<SendOutlined />}
            onClick={() => openDialog("offer")}
          >
            {t("Send to customer")}
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: "sign",
                  icon: <EditOutlined />,
                  label: t("Customer signs now"),
                  onClick: () => openDialog("sign")
                },
                ...menu
              ]
            }}
            trigger={["click"]}
            placement="topRight"
          >
            <Button size="large" icon={<MoreOutlined />} aria-label={t("More")} />
          </Dropdown>
        </div>
      )}

      {/* ---- Dialogs ---- */}
      <Modal
        rootClassName="change-orders-page"
        open={["announce", "offer", "remind", "decide", "withdraw"].includes(dialog)}
        title={
          {
            announce: t("Send cost notice"),
            offer: t("Send change order to the customer"),
            remind: t("Send reminder"),
            decide: t("Record the customer's decision"),
            withdraw: t("Withdraw change order")
          }[dialog]
        }
        okText={
          {
            announce: dialogValues.send ? t("Send") : t("Save"),
            offer: dialogValues.send ? t("Send") : t("Create link"),
            remind: t("Send"),
            decide: t("Save"),
            withdraw: t("Withdraw")
          }[dialog]
        }
        okButtonProps={dialog === "withdraw" ? { danger: true } : undefined}
        cancelText={t("Cancel")}
        confirmLoading={busy}
        onOk={run}
        onCancel={() => setDialog(null)}
        destroyOnClose
      >
        {dialog === "announce" && (
          <Space direction="vertical" className="co-full">
            <Typography.Paragraph type="secondary">
              {t(
                "A formal notice that this work costs extra, sent before you start it. The amount is optional."
              )}
            </Typography.Paragraph>
            <Checkbox
              checked={dialogValues.send}
              onChange={e => setValue({ send: e.target.checked })}
            >
              {t("Send by email")}
            </Checkbox>
            {dialogValues.send && (
              <Input
                type="email"
                inputMode="email"
                value={dialogValues.email}
                placeholder={t("Email address")}
                onChange={e => setValue({ email: e.target.value })}
              />
            )}
            {!dialogValues.send && (
              <Typography.Text type="secondary">
                {t("Only the date is recorded. Hand over the PDF yourself.")}
              </Typography.Text>
            )}
          </Space>
        )}
        {(dialog === "offer" || dialog === "remind") && (
          <Space direction="vertical" className="co-full">
            <Typography.Paragraph type="secondary">
              {t(
                "The customer gets the PDF and a link to approve or reject it, valid for 21 days."
              )}
            </Typography.Paragraph>
            {dialog === "offer" && (
              <Checkbox
                checked={dialogValues.send}
                onChange={e => setValue({ send: e.target.checked })}
              >
                {t("Send by email")}
              </Checkbox>
            )}
            {(dialog === "remind" || dialogValues.send) && (
              <Input
                type="email"
                inputMode="email"
                value={dialogValues.email}
                placeholder={t("Email address")}
                onChange={e => setValue({ email: e.target.value })}
              />
            )}
            {dialog === "offer" && (
              <Form.Item label={t("Answer within (days)")} className="mb-0">
                <InputNumber
                  min={1}
                  max={90}
                  value={dialogValues.responseDays}
                  onChange={value => setValue({ responseDays: value || 14 })}
                />
              </Form.Item>
            )}
          </Space>
        )}
        {dialog === "decide" && (
          <Space direction="vertical" className="co-full">
            <Radio.Group
              value={dialogValues.status}
              onChange={e => setValue({ status: e.target.value })}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="approved">{t("Approved")}</Radio.Button>
              <Radio.Button value="rejected">{t("Rejected")}</Radio.Button>
              {state !== "ordered" && (
                <Radio.Button value="ordered">{t("Ordered, price open")}</Radio.Button>
              )}
            </Radio.Group>
            {dialogValues.status === "ordered" && (
              <Typography.Text type="secondary">
                {t(
                  "The customer wants the work done but has not agreed to the price. You may start; send the priced change order."
                )}
              </Typography.Text>
            )}
            <Input
              value={dialogValues.name}
              maxLength={120}
              placeholder={t("Who decided")}
              onChange={e => setValue({ name: e.target.value })}
            />
            <Input.TextArea
              value={dialogValues.note}
              maxLength={2000}
              autoSize={{ minRows: 2 }}
              placeholder={t("How: letter of 02.10., email, phone call …")}
              onChange={e => setValue({ note: e.target.value })}
            />
            {dialogValues.status !== "ordered" && (
              <Checkbox
                checked={dialogValues.emailCopy}
                onChange={e => setValue({ emailCopy: e.target.checked })}
              >
                {t("Send the confirmation to the customer by email")}
              </Checkbox>
            )}
          </Space>
        )}
        {dialog === "withdraw" && (
          <Input.TextArea
            autoFocus
            value={dialogValues.reason}
            maxLength={1000}
            autoSize={{ minRows: 2 }}
            placeholder={t("Reason")}
            onChange={e => setValue({ reason: e.target.value })}
          />
        )}
      </Modal>

      <Modal
        rootClassName="change-orders-page"
        open={dialog === "sign"}
        width={760}
        title={
          signStep === "review"
            ? t("Please check the change order")
            : t("Signature of the customer")
        }
        onCancel={() => setDialog(null)}
        maskClosable={false}
        destroyOnClose
        footer={
          signStep === "review"
            ? [
                <Button key="cancel" onClick={() => setDialog(null)}>
                  {t("Cancel")}
                </Button>,
                <Button
                  key="next"
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => setSignStep("sign")}
                >
                  {t("Approve and sign")}
                </Button>
              ]
            : null
        }
      >
        {signStep === "review" ? (
          <ChangeOrderPreview view={previewView} photoSrc={photoId => photoUrl(id, photoId)} />
        ) : (
          <div className="work-reports-page">
            <SignatureForm
              defaultEmail={data.clientInfo?.email || ""}
              showLocation={false}
              submitting={busy}
              onSubmit={signOnSite}
              onBack={() => setSignStep("review")}
            />
          </div>
        )}
      </Modal>

      <Modal
        rootClassName="change-orders-page"
        open={dialog === "link"}
        title={t("Approval link")}
        onCancel={() => setDialog(null)}
        footer={[
          <Button key="share" icon={<ShareAltOutlined />} onClick={share}>
            {t("Share")}
          </Button>,
          <Button key="ok" type="primary" onClick={() => setDialog(null)}>
            {t("Done")}
          </Button>
        ]}
      >
        {created?.sentTo && (
          <Alert
            type="success"
            showIcon
            className="mb-3"
            message={t("Sent to {{email}}.", { email: created.sentTo })}
          />
        )}
        <Typography.Paragraph type="secondary">
          {t("You can also send the link by WhatsApp or SMS. It is shown only now.")}
        </Typography.Paragraph>
        <Space.Compact className="co-full">
          <Input readOnly value={created?.url} />
          <Button
            icon={<CopyOutlined />}
            aria-label={t("Copy")}
            onClick={async () => {
              await navigator.clipboard?.writeText(created.url);
              Success(t("Link copied"));
            }}
          />
        </Space.Compact>
      </Modal>
    </div>
  );
};

export default ChangeOrderEditor;
